import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { referralService } from '../services/referralService'
import { formatDate } from '../utils/locale'
import { ReferralForm } from './ReferralForm'
import './ReferralDetail.css'

interface ReferralDetailProps {
  referralId: string
  patientId: string
  onBack?: () => void
  onUpdate?: () => void
}

export function ReferralDetail({
  referralId,
  patientId,
  onBack,
  onUpdate,
}: ReferralDetailProps) {
  const queryClient = useQueryClient()
  const [isEditing, setIsEditing] = useState(false)

  const { data: referral, isLoading, error } = useQuery({
    queryKey: ['referral', referralId],
    queryFn: () => referralService.getById(referralId),
  })

  const sendMutation = useMutation({
    mutationFn: (data?: { communicationMethod?: string; healthmailMessageId?: string }) =>
      referralService.send(referralId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['referral', referralId] })
      queryClient.invalidateQueries({ queryKey: ['referrals'] })
      onUpdate?.()
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => referralService.delete(referralId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['referrals'] })
      onBack?.()
    },
  })

  const handleSend = () => {
    if (window.confirm('Send this referral?')) {
      sendMutation.mutate({
        communicationMethod: referral?.communicationMethod,
      })
    }
  }

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to cancel this referral?')) {
      deleteMutation.mutate()
    }
  }

  if (isLoading) {
    return <div className="referral-detail-loading">Loading referral...</div>
  }

  if (error || !referral) {
    return <div className="referral-detail-error">Error loading referral</div>
  }

  if (isEditing) {
    return (
      <div className="referral-detail-edit">
        <ReferralForm
          patientId={patientId}
          consultationId={referral.consultationId}
          referral={referral}
          onSuccess={() => {
            setIsEditing(false)
            queryClient.invalidateQueries({ queryKey: ['referral', referralId] })
            onUpdate?.()
          }}
          onCancel={() => setIsEditing(false)}
        />
      </div>
    )
  }

  return (
    <div className="referral-detail">
      <div className="referral-detail-header">
        <h2>Referral Details</h2>
        <div className="referral-detail-actions">
          <button className="btn-secondary" onClick={onBack}>
            Back
          </button>
          {referral.status === 'draft' && (
            <>
              <button className="btn-secondary" onClick={() => setIsEditing(true)}>
                Edit
              </button>
              <button className="btn-primary" onClick={handleSend} disabled={sendMutation.isPending}>
                {sendMutation.isPending ? 'Sending...' : 'Send Referral'}
              </button>
            </>
          )}
          {referral.status !== 'cancelled' && (
            <button className="btn-danger" onClick={handleDelete} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending ? 'Cancelling...' : 'Cancel Referral'}
            </button>
          )}
        </div>
      </div>

      <div className="referral-detail-content">
        <div className="referral-detail-section">
          <h3>Referral Information</h3>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Referral Date</label>
              <div>{formatDate(referral.referralDate)}</div>
            </div>
            <div className="detail-item">
              <label>Priority</label>
              <div>
                <span className={`priority-badge priority-${referral.priority}`}>
                  {referral.priority}
                </span>
              </div>
            </div>
            <div className="detail-item">
              <label>Status</label>
              <div>
                <span className={`status-badge status-${referral.status}`}>
                  {referral.status}
                </span>
              </div>
            </div>
            <div className="detail-item">
              <label>Communication Method</label>
              <div>{referral.communicationMethod}</div>
            </div>
            <div className="detail-item">
              <label>Reason for Referral</label>
              <div>{referral.reason}</div>
            </div>
          </div>
        </div>

        <div className="referral-detail-section">
          <h3>Provider Information</h3>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Referred To</label>
              <div>
                {referral.referredToProviderName}
                {referral.providerDetails?.organization && ` - ${referral.providerDetails.organization}`}
              </div>
            </div>
            {referral.providerDetails?.specialty && (
              <div className="detail-item">
                <label>Specialty</label>
                <div>{referral.providerDetails.specialty}</div>
              </div>
            )}
            {referral.providerDetails?.address && (
              <div className="detail-item full-width">
                <label>Address</label>
                <div>
                  {referral.providerDetails.address.street}
                  {referral.providerDetails.address.city && `, ${referral.providerDetails.address.city}`}
                  {referral.providerDetails.address.state && `, ${referral.providerDetails.address.state}`}
                  {referral.providerDetails.address.postalCode && ` ${referral.providerDetails.address.postalCode}`}
                  {referral.providerDetails.address.country && `, ${referral.providerDetails.address.country}`}
                </div>
              </div>
            )}
            {referral.providerDetails?.phone && (
              <div className="detail-item">
                <label>Phone</label>
                <div>{referral.providerDetails.phone}</div>
              </div>
            )}
            {referral.providerDetails?.email && (
              <div className="detail-item">
                <label>Email</label>
                <div>{referral.providerDetails.email}</div>
              </div>
            )}
            {referral.providerDetails?.healthmailAddress && (
              <div className="detail-item">
                <label>Healthmail Address</label>
                <div>{referral.providerDetails.healthmailAddress}</div>
              </div>
            )}
          </div>
        </div>

        {referral.clinicalDetails && (
          <div className="referral-detail-section">
            <h3>Clinical Details</h3>
            {referral.clinicalDetails.presentingComplaint && (
              <div className="detail-item full-width">
                <label>Presenting Complaint</label>
                <div>{referral.clinicalDetails.presentingComplaint}</div>
              </div>
            )}
            {referral.clinicalDetails.historyOfPresentingComplaint && (
              <div className="detail-item full-width">
                <label>History of Presenting Complaint</label>
                <div>{referral.clinicalDetails.historyOfPresentingComplaint}</div>
              </div>
            )}
            {referral.clinicalDetails.pastMedicalHistory && (
              <div className="detail-item full-width">
                <label>Past Medical History</label>
                <div>{referral.clinicalDetails.pastMedicalHistory}</div>
              </div>
            )}
            {referral.clinicalDetails.currentMedications && referral.clinicalDetails.currentMedications.length > 0 && (
              <div className="detail-item full-width">
                <label>Current Medications</label>
                <div>
                  {Array.isArray(referral.clinicalDetails.currentMedications)
                    ? referral.clinicalDetails.currentMedications.join(', ')
                    : referral.clinicalDetails.currentMedications}
                </div>
              </div>
            )}
            {referral.clinicalDetails.allergies && referral.clinicalDetails.allergies.length > 0 && (
              <div className="detail-item full-width">
                <label>Allergies</label>
                <div>
                  {Array.isArray(referral.clinicalDetails.allergies)
                    ? referral.clinicalDetails.allergies.join(', ')
                    : referral.clinicalDetails.allergies}
                </div>
              </div>
            )}
            {referral.clinicalDetails.examinationFindings && (
              <div className="detail-item full-width">
                <label>Examination Findings</label>
                <div>{referral.clinicalDetails.examinationFindings}</div>
              </div>
            )}
            {referral.clinicalDetails.investigations && (
              <div className="detail-item full-width">
                <label>Investigations</label>
                <div>{referral.clinicalDetails.investigations}</div>
              </div>
            )}
            {referral.clinicalDetails.diagnosis && referral.clinicalDetails.diagnosis.length > 0 && (
              <div className="detail-item full-width">
                <label>Diagnosis</label>
                <div>
                  {Array.isArray(referral.clinicalDetails.diagnosis)
                    ? referral.clinicalDetails.diagnosis.join(', ')
                    : referral.clinicalDetails.diagnosis}
                </div>
              </div>
            )}
            {referral.clinicalDetails.treatmentToDate && (
              <div className="detail-item full-width">
                <label>Treatment to Date</label>
                <div>{referral.clinicalDetails.treatmentToDate}</div>
              </div>
            )}
            {referral.clinicalDetails.specificQuestions && (
              <div className="detail-item full-width">
                <label>Specific Questions for Receiving Provider</label>
                <div>{referral.clinicalDetails.specificQuestions}</div>
              </div>
            )}
          </div>
        )}

        {referral.insuranceInformation && (
          <div className="referral-detail-section">
            <h3>Insurance Information</h3>
            <div className="detail-grid">
              {referral.insuranceInformation.insuranceProvider && (
                <div className="detail-item">
                  <label>Insurance Provider</label>
                  <div>{referral.insuranceInformation.insuranceProvider}</div>
                </div>
              )}
              {referral.insuranceInformation.policyNumber && (
                <div className="detail-item">
                  <label>Policy Number</label>
                  <div>{referral.insuranceInformation.policyNumber}</div>
                </div>
              )}
              {referral.insuranceInformation.groupNumber && (
                <div className="detail-item">
                  <label>Group Number</label>
                  <div>{referral.insuranceInformation.groupNumber}</div>
                </div>
              )}
              {referral.insuranceInformation.authorizationNumber && (
                <div className="detail-item">
                  <label>Authorization Number</label>
                  <div>{referral.insuranceInformation.authorizationNumber}</div>
                </div>
              )}
              {referral.insuranceInformation.expiryDate && (
                <div className="detail-item">
                  <label>Expiry Date</label>
                  <div>{formatDate(referral.insuranceInformation.expiryDate)}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {referral.communicationStatus && (
          <div className="referral-detail-section">
            <h3>Communication Status</h3>
            <div className="detail-grid">
              {referral.communicationStatus.sentAt && (
                <div className="detail-item">
                  <label>Sent At</label>
                  <div>{formatDate(referral.communicationStatus.sentAt)}</div>
                </div>
              )}
              {referral.communicationStatus.acknowledgedAt && (
                <div className="detail-item">
                  <label>Acknowledged At</label>
                  <div>{formatDate(referral.communicationStatus.acknowledgedAt)}</div>
                </div>
              )}
              {referral.communicationStatus.healthmailMessageId && (
                <div className="detail-item">
                  <label>Healthmail Message ID</label>
                  <div>{referral.communicationStatus.healthmailMessageId}</div>
                </div>
              )}
              {referral.communicationStatus.errorMessage && (
                <div className="detail-item full-width">
                  <label>Error Message</label>
                  <div className="error-text">{referral.communicationStatus.errorMessage}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {referral.followUpRequired && (
          <div className="referral-detail-section">
            <h3>Follow-up</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <label>Follow-up Required</label>
                <div>Yes</div>
              </div>
              {referral.followUpDate && (
                <div className="detail-item">
                  <label>Follow-up Date</label>
                  <div>{formatDate(referral.followUpDate)}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {referral.notes && (
          <div className="referral-detail-section">
            <h3>Additional Notes</h3>
            <div className="detail-item full-width">
              <div>{referral.notes}</div>
            </div>
          </div>
        )}

        <div className="referral-detail-section">
          <h3>Metadata</h3>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Created At</label>
              <div>{formatDate(referral.createdAt)}</div>
            </div>
            <div className="detail-item">
              <label>Updated At</label>
              <div>{formatDate(referral.updatedAt)}</div>
            </div>
            {referral.referringProviderName && (
              <div className="detail-item">
                <label>Referring Provider</label>
                <div>{referral.referringProviderName}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
